import { Autocomplete, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { formatarCEP, formatarInputCPF, formatarTelefone } from "../../../../../helpers/masks";
import { useMemo } from "react";
import { useQuery } from "react-query";
import SearchIcon from '@mui/icons-material/Search';
import axiosClient from "../../../../../lib/axios";
import { desformatarCEP } from "../../../../../helpers/unmask";
import { ViaCEPResposta } from "../../../../../types/externas/viacep-resultado";

export default function AtualizacaoCadastral() {

    const { control, getFieldState, watch, getValues, setValue } = useFormContext();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])
    const opcoesRadio = useMemo(() => [
        { value: "Sim", label: "Sim" },
        { value: "Não", label: "Não" },
    ], [])

    const possuiConjuge = watch("attCadastral.possuiConjuge") === "Sim"

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch("attCadastral.uf")],
        queryFn: async () => {
            if (watch("attCadastral.uf") === "") return []

            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues("attCadastral.uf")
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues("attCadastral")
        if (cep === "") return

        const unmaskCep = desformatarCEP(cep)

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue("attCadastral.uf", uf)
            setValue("attCadastral.bairro", bairro)
            setValue("attCadastral.cidade", localidade)
            setValue("attCadastral.endereco", logradouro)
        } catch (error) {
            console.error('Erro ao buscar endereço por CEP:', error)
            return
        }
    }
    //

    return (
        <Grid container spacing={2}>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="attCadastral.telefone"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Telefone"
                            variant="outlined"
                            required
                            fullWidth
                            onChange={(e) => {
                                field.onChange(formatarTelefone(e.target.value))
                            }}
                            error={getFieldState('attCadastral.telefone').invalid}
                            helperText={getFieldState('attCadastral.telefone').error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
                <Controller
                    name="attCadastral.email"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Email"
                            variant="outlined"
                            fullWidth
                            error={getFieldState('attCadastral.email').invalid}
                            helperText={getFieldState('attCadastral.email').error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name="attCadastral.cep"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="CEP"
                            variant="outlined"
                            fullWidth
                            required
                            onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                            onBlur={handlePesquisarCep}
                            error={getFieldState('attCadastral.cep').invalid}
                            helperText={getFieldState('attCadastral.cep').error?.message || ""}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handlePesquisarCep}
                                            edge="end"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name="attCadastral.endereco"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Endereço"
                            variant="outlined"
                            required
                            fullWidth
                            error={getFieldState('attCadastral.endereco').invalid}
                            helperText={getFieldState('attCadastral.endereco').error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name="attCadastral.numero"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Número"
                            variant="outlined"
                            required
                            fullWidth
                            onChange={(e) => {
                                field.onChange(e.target.value)
                            }}
                            error={getFieldState('attCadastral.numero').invalid}
                            helperText={getFieldState('attCadastral.numero').error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name="attCadastral.complemento"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Complemento"
                            variant="outlined"
                            fullWidth
                            error={getFieldState('attCadastral.complemento').invalid}
                            helperText={getFieldState('attCadastral.complemento').error?.message || ""}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name="attCadastral.bairro"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Bairro"
                            variant="outlined"
                            required
                            fullWidth
                            error={getFieldState('attCadastral.bairro').invalid}
                            helperText={getFieldState('attCadastral.bairro').error?.message || ""}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name="attCadastral.uf"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <FormControl fullWidth {...field} error={getFieldState('attCadastral.uf').invalid} required>
                            <InputLabel id="uf-select">UF</InputLabel>
                            <Select
                                labelId="uf-select"
                                value={field.value}
                                label="UF"
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                {ufs.map((uf, index) => (
                                    <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                ))}
                            </Select>
                            {getFieldState('attCadastral.uf').error && (
                                <FormHelperText>{getFieldState('attCadastral.uf').error?.message}</FormHelperText>
                            )}
                        </FormControl>
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name="attCadastral.cidade"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <>
                            <Autocomplete
                                {...field}
                                fullWidth
                                disablePortal
                                options={dataCidades}
                                onChange={(_, v) => field.onChange(v)}
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Cidade"
                                        required
                                        error={Boolean(getFieldState('attCadastral.cidade').invalid)}
                                        helperText={getFieldState('attCadastral.cidade').error?.message || ""}
                                    />
                                }
                            />
                        </>
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Controller
                    name="attCadastral.possuiConjuge"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <FormControl {...field}>
                            <FormLabel id="possuiConjuge">Possui conjuge?</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="possuiConjuge"
                                value={getValues("attCadastral.possuiConjuge")}
                                onChange={(e) => field.onChange(e.target.checked)}
                            >
                                {
                                    opcoesRadio.map((opcao, index) => (
                                        <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                    ))
                                }
                            </RadioGroup>
                        </FormControl>
                    )}
                />
            </Grid>

            {
                possuiConjuge &&
                <Grid size={{ xs: 12, md: 8 }}>
                    <Controller
                        name="attCadastral.conjuge"
                        control={control}
                        rules={{ required: possuiConjuge ? 'Campo obrigatório' : false }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Conjuge/Companheiro(a)"
                                variant="outlined"
                                fullWidth
                                error={getFieldState('attCadastral.conjuge').invalid}
                                helperText={getFieldState('attCadastral.conjuge').error?.message || ""}
                            />
                        )}
                    />
                </Grid>
            }

            {
                possuiConjuge &&
                <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                        name="attCadastral.document"
                        control={control}
                        rules={{ required: possuiConjuge ? 'Campo obrigatório' : false }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="CPF"
                                variant="outlined"
                                fullWidth
                                onChange={(e) => {
                                    field.onChange(formatarInputCPF(e.target.value))
                                }}
                                error={getFieldState('attCadastral.document').invalid}
                                helperText={getFieldState('attCadastral.document').error?.message || ""}
                            />
                        )}
                    />
                </Grid>
            }
        </Grid>
    )
}

interface ICidades {
    dataCidades: Array<string>
}